import { useMemo } from 'react';
import {
  computeStrandedCapacity,
  computeStrandedTimeline,
  strandedIncomeAttribution,
  fmt,
  fmtMoney,
  type SeparationState,
  monthLabel,
} from '@integrate/core';
import { MonthChart } from './MonthChart.js';
import { StatTile } from '../ui/StatTile.js';

/**
 * RemainCo's stranded cost over time, ported from `renderStranded` (orgchart.html:7052–7106).
 *
 * The story is three series and it is worth naming them, because the copy this pane replaces named the
 * wrong one. GROSS exposure steps DOWN as each mitigation lands. TSA fee INCOME steps down as each
 * received agreement ends. It is the NET — gross less income — that steps UP, and that is what the pane
 * exists to show: the moment the temporary cover runs out.
 *
 * Read-only, and takes no `onDispatch`. The original's version of this pane ended in an editable carrier
 * list that dispatched from inside a renderer, capped at the first sixty carriers while advertising the
 * full count. The editing stays where it already is — the RemainCo phase panel's mitigation plan, which
 * is mounted behind this modal — and this links to it.
 *
 * Lens-free by construction: `computeStrandedTimeline` and `computeStrandedCapacity` read the roster and
 * the design, and nothing about who is looking. The phase panel's carrier list DOES follow the chart's
 * filters, so the two legitimately differ; the note at the foot says so rather than leaving a reader to
 * find two counts and no explanation.
 */
export function StrandedPane({
  state,
  metric,
  onMetric,
  levelKey,
  onLevelKey,
  onOpenPlan,
  onOpenAssumptions,
}: {
  state: SeparationState;
  /**
   * Owned by the modal, so a tab switch does not discard it — the app kept `_strMetric` and
   * `_strLevelIdx` at module scope for exactly that reason.
   *
   * Two values, not three, and its Cost is the LOADED one: `d × loadedCost`. That is why the control says
   * "Loaded cost" — the phase bridge's Cost is raw salary, and one word for two weights across two tabs
   * of one modal is the P4l-a defect in label form.
   */
  metric: 'cost' | 'fte';
  onMetric(next: 'cost' | 'fte'): void;
  levelKey: string | null;
  onLevelKey(next: string | null): void;
  onOpenPlan(): void;
  onOpenAssumptions(): void;
}): JSX.Element {
  const T = useMemo(() => computeStrandedTimeline(state), [state]);
  // Where that income actually sits, which the row below it used to assert without computing.
  const A = useMemo(() => strandedIncomeAttribution(state), [state]);
  const C = useMemo(() => computeStrandedCapacity(state, { levelKey }), [state, levelKey]);

  /*
    No Remain-side carriers is not an empty pane: this surface owns the CARVE side too.

    `excessCost` — undedicated capacity travelling into the carve-out with partly-dedicated people — is
    computed here and printed nowhere else in the client. The first version of this gate returned early on
    `!T.carriers.length`, so a design where everybody staying is fully undedicated said "nothing to draw"
    over millions of euros of capacity crossing the perimeter. The frozen app gated on whether there was
    ANY dedication data at all and printed the carve-side figure unconditionally (orgchart.html:7053,
    :7105).
  */
  if (!T.carriers.length) {
    return (
      <section data-testid="stranded-pane">
        <p className="muted" data-testid="stranded-empty">
          Nobody carries stranded capacity, so there is no RemainCo exposure to draw. A carrier is
          somebody staying in RemainCo with a numeric dedication above zero —{' '}
          {C.tot.noData > 0
            ? `${fmt(C.tot.noData)} Remain-side ${C.tot.noData === 1 ? 'person has' : 'people have'} no dedication recorded, which is a data gap rather than a zero.`
            : 'and every Remain-side person here is fully dedicated to the retained business.'}
        </p>
        <ExcessNote tot={C.tot} />
      </section>
    );
  }

  const months = T.monthly.map((m) => m.m);
  const rows = [...C.groups.entries()].sort(
    (a, b) => b[1].strandedCost + b[1].excessCost - (a[1].strandedCost + a[1].excessCost),
  );
  /*
    The unit travels with the number. `fmt(fte, 1)` alone gave a bare "42.5" in a table whose other
    reading is euros, so a screenshot of one mode is indistinguishable from the other; the app's own
    formatter appended ' FTE' (orgchart.html:7066).
  */
  const fmtMetric = (cost: number, fte: number): string =>
    metric === 'cost' ? fmtMoney(cost) : `${fmt(fte, 1)} FTE`;

  return (
    <section data-testid="stranded-pane">
      {/*
        A real bridge: gross − rightsized share − redeployed = residual, and it closes.

        The original printed five tiles that read as a subtraction, minus sign and all, and did not
        reconcile — it put the FULL loaded rightsizing saving in the middle of a column of stranded
        shares, so the column came out below the residual beside it by exactly `overRemovalRunRate`. Two
        run-rates measuring different things subtracted from one. The cash saving is a real number and is
        below, where it reads as a cash effect rather than a term in the bridge.
      */}
      <div className="stat-grid" data-testid="stranded-tiles">
        {/*
          The bridge is in MONEY, whatever the table below is measured in.

          It was `fmtMetric` here and hard-coded money on the three tiles that bridge from it, so in FTE
          mode a labelled subtraction read "42.5 − €480,000 − — = €1,200,000": two units in one column of
          four, under minus signs. The FTE is a real figure and is in the sub-line, which is where the app
          put it too.
        */}
        <StatTile
          label="Gross stranded"
          valueTestId="stranded-gross"
          value={fmtMoney(C.tot.strandedCost)}
          sub={`p.a. · ${fmt(C.tot.strandedN)} carriers · ${fmt(C.tot.strandedFte, 1)} FTE`}
        />
        <StatTile
          label="− Rightsized"
          valueTestId="stranded-rightsized-share"
          value={T.rightsizedN ? fmtMoney(T.rightsizedStrandedShare) : '—'}
          sub={`${fmt(T.rightsizedN)} ${T.rightsizedN === 1 ? 'person' : 'people'} · their stranded share`}
        />
        <StatTile
          label="− Redeployed"
          valueTestId="stranded-redeployed"
          value={T.redeployedN ? fmtMoney(T.redeployedRunRate) : '—'}
          sub={`${fmt(T.redeployedN)} ${T.redeployedN === 1 ? 'person' : 'people'} · no cash effect`}
        />
        <StatTile
          label="= Residual steady state"
          valueTestId="stranded-residual"
          value={fmtMoney(T.residualRunRate)}
          sub={`p.a. · ${fmt(T.residualN)} unmitigated`}
          danger={T.residualRunRate > 0.004}
        />
      </div>

      {/*
        "Covered by" was the claim, and nothing in the model supported it.

        The netting sums income across every received agreement regardless of where the capacity sits, so
        an IT agreement's fee offsets stranded cost in Finance — and the caption called that coverage. The
        arithmetic is right and the word was not: what it does is OFFSET, at the entity level.

        The comparison the old word implied is printed underneath instead, because it is computable: the
        register's `area` datalist is built from the first grouping level, so an area typed through the
        control is a level value. It is not enforced, which is exactly why the two remainders are stated
        rather than folded away — an area naming nothing is a register that disagrees with the roster, an
        agreement with no area is a register nobody finished, and they are different problems.
      */}
      <div className="sp-row">
        <span className="k">Offset by TSA income, temporarily</span>
        {/* The annual rate as a number, so a reader of the split below can tie it to the total
            without parsing a sentence — the two come from different functions on purpose. */}
        <span
          className="v"
          data-testid="stranded-income"
          data-annual={Math.round(T.tsaIncomeMonthly * 12)}
        >
          {T.tsaIncomeMonthly
            ? `${fmtMoney(T.tsaIncomeMonthly * 12)} p.a. now, stepping down to nothing by M${T.tsaIncomeMonths}`
            : 'no received TSAs'}
        </span>
      </div>
      {T.tsaIncomeMonthly > 0 && (
        <div
          className="sp-row"
          data-testid="stranded-income-attribution"
          data-matched={Math.round(A.matchedIncome)}
          data-unmatched={Math.round(A.unmatchedIncome)}
          data-unattributed={Math.round(A.unattributedIncome)}
          data-uncovered={Math.round(A.strandedWithoutIncome)}
          title="Matched where the agreement's Area names a group that carries stranded cost. Area is free text, so a file can carry anything — which is why the rest is shown rather than assumed."
        >
          <span className="k">↳ of which where the cost is</span>
          <span className="v">
            {A.levelKey === null ? (
              'no hierarchy to place it against'
            ) : A.matchedIncome > 0 ? (
              <>
                {fmtMoney(A.matchedIncome)} from {fmt(A.matchedAgreementsN)} of{' '}
                {fmt(A.agreementsN)} agreements
                {A.strandedWithoutIncome > 0.004 && (
                  <span className="muted">
                    {' '}
                    · {fmtMoney(A.strandedWithoutIncome)} stranded with none
                  </span>
                )}
              </>
            ) : (
              <span className="bad">none of it can be placed</span>
            )}
          </span>
        </div>
      )}
      {T.rightsizedN > 0 && (
        <>
          <div className="sp-row" title="Months × base salary, not loaded — see the assumptions">
            <span className="k">One-off severance</span>
            <span className="v" data-testid="stranded-severance">
              {fmtMoney(T.severanceOneOff)}
            </span>
          </div>
          <div
            className="sp-row"
            title="A rightsized carrier was only partly dedicated to the divested business; the rest of their capacity still served RemainCo and leaves with them"
          >
            <span className="k">Cash saving from the rightsizing</span>
            <span className="v" data-testid="stranded-cash-saving">
              {fmtMoney(T.rightsizeSavingRunRate)} p.a.
            </span>
          </div>
          {T.overRemovalRunRate > 0.004 && (
            <div className="sp-row">
              <span className="k">⚠ of which over-removal (backfill risk)</span>
              <span className="v bad" data-testid="stranded-over-removal">
                {fmtMoney(T.overRemovalRunRate)} p.a.
              </span>
            </div>
          )}
        </>
      )}

      <MonthChart
        testId="stranded-monthly"
        caption="Net stranded exposure"
        unit="€ per month, months after Day 1"
        months={months}
        labelOf={(m) => monthLabel(m, state.assumptions)}
        scaleStep={T.horizon > 18 ? 6 : 3}
        format={fmtMoney}
        ariaLabel={
          // The prose IS the chart for a screen reader, so it must describe the deferred shape
          // when there is one: gross ramps IN as late countries close, not only down.
          `Net RemainCo stranded cost over ${T.horizon} months after Day 1. Gross exposure of ` +
          `${fmtMoney(T.grossRunRate / 12)} per month ` +
          (T.carriers.some((c) => c.closeMonth > 0)
            ? `ramps in as each deferred country closes and steps down as each mitigation takes effect; `
            : `steps down as each mitigation takes effect; `) +
          `TSA fee income starts at ${fmtMoney(T.tsaIncomeMonthly)} per month and is gone by M${T.tsaIncomeMonths}, ` +
          `so the net steps up. Residual steady state is ${fmtMoney(T.residualRunRate)} a year. ` +
          `One-off severance is drawn in its own band with its own scale.`
        }
        series={[
          {
            key: 'net',
            label: 'Net stranded (gross − TSA income)',
            side: 'up',
            color: 'danger',
            values: T.monthly.map((m) => Math.max(0, m.net)),
          },
          {
            key: 'surplus',
            label: 'Income surplus (TSA income > gross)',
            side: 'down',
            color: 'success',
            values: T.monthly.map((m) => Math.max(0, -m.net)),
          },
        ]}
        markers={[
          {
            key: 'severance',
            label: 'Severance (one-off, own scale)',
            color: 'pink',
            values: T.monthly.map((m) => m.severance),
          },
        ]}
        note={
          'The bars are a monthly run-rate; the tiles above are annual. Severance is one-off cash, so it ' +
          'has its own band and its own scale — summed into the same column it would set the height of a ' +
          'per-month chart with a single payment.'
        }
      />

      <div className="sp-row" style={{ marginTop: 14 }}>
        <label htmlFor="stranded-level" className="k">
          Break down by
        </label>
        <select
          id="stranded-level"
          data-testid="stranded-level"
          value={levelKey ?? ''}
          onChange={(e) => onLevelKey(e.target.value || null)}
        >
          <option value="">Whole organisation</option>
          {state.groupingLevels.map((l) => (
            <option key={l.key} value={l.key}>
              {l.label}
            </option>
          ))}
        </select>
        <select
          data-testid="stranded-metric"
          value={metric}
          onChange={(e) => onMetric(e.target.value as 'cost' | 'fte')}
        >
          {/* The app's own label. This pane's Cost is d × LOADED cost; the phase bridge's is raw salary. */}
          <option value="cost">Loaded cost</option>
          <option value="fte">FTE</option>
        </select>
      </div>

      <div className="table-scroll">
        <table className="tsa-table" data-testid="stranded-by-level">
          <thead>
            <tr>
              <th>{levelKey ? state.groupingLevels.find((l) => l.key === levelKey)?.label : 'All'}</th>
              <th className="num">Stranded</th>
              <th className="num">Mitigated</th>
              <th className="num">Residual</th>
              <th className="num">Carve-side excess</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, g]) => (
              <tr
                key={key}
                data-testid={`stranded-group-${key}`}
                data-stranded={g.strandedCost}
                data-residual={g.residualCost}
                data-excess={g.excessCost}
              >
                <td>{key}</td>
                <td className="num">
                  {fmtMetric(g.strandedCost, g.strandedFte)}
                  {g.strandedN > 0 && <span className="pill">{fmt(g.strandedN)}</span>}
                </td>
                <td className="num">{fmtMetric(g.mitigatedCost, g.mitigatedFte)}</td>
                <td className="num">{fmtMetric(g.residualCost, g.residualFte)}</td>
                <td className="num">{fmtMetric(g.excessCost, g.excessFte)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*
        Two conventions this table follows that the chart beside it does not, both faithful and both worth
        naming rather than quietly reconciling.
      */}
      <p className="muted" data-testid="stranded-caveats">
        These rows group on the <strong>raw</strong> level value, so a per-scenario group rename does not
        move them and the labels can differ from the chart&rsquo;s; a blank value collects under{' '}
        <code>(none)</code>. <strong>Carve-side excess</strong> is the mirror quantity — the undedicated
        share of somebody who went to the carve-out, which the carve-out is now paying for.
        {C.tot.excessN > 0 && (
          <>
            {' '}
            {fmt(C.tot.excessN)} {C.tot.excessN === 1 ? 'person carries' : 'people carry'} it,
            {C.tot.excessExitN > 0
              ? ` ${fmt(C.tot.excessExitN)} of whom ${C.tot.excessExitN === 1 ? 'is' : 'are'} already marked to exit at Day 2.`
              : ' none of whom are marked to exit at Day 2.'}
          </>
        )}
        {C.tot.noData > 0 && (
          <>
            {' '}
            {fmt(C.tot.noData)} Remain-side {C.tot.noData === 1 ? 'person has' : 'people have'} no
            dedication recorded and are counted nowhere above — a data gap, not a zero.
          </>
        )}
      </p>

      <ExcessNote tot={C.tot} />

      <p className="muted">
        This pane is unfiltered: it prices the whole design, whatever the chart behind it is narrowed to.
        The carrier list on the RemainCo panel follows your filters instead, so the two can legitimately
        report different counts. Loaded cost is salary ×{' '}
        <button type="button" className="linkish" data-testid="stranded-asm" onClick={onOpenAssumptions}>
          {Number(state.assumptions.loadedCostMultiplier) || 1}
        </button>
        ; severance uses the months rules from the same place.
      </p>

      <button className="primary" data-testid="stranded-open-plan" onClick={onOpenPlan}>
        Edit the mitigation plan →
      </button>
    </section>
  );
}

/**
 * The CARVE side: undedicated capacity that travels into the carve-out with partly-dedicated people.
 *
 * Printed by no other surface in the client — grep `excessCost` — and the app printed it unconditionally
 * at the foot of this pane (orgchart.html:7105) including the count already marked for a Day 2 exit,
 * which is what makes it actionable: reverse-TSA backing, or a rightsizing candidate.
 *
 * Its own component because the pane has two exits: with Remain-side carriers and without. The version
 * without used to return a single sentence and drop this figure entirely.
 */
function ExcessNote({
  tot,
}: {
  tot: { excessCost: number; excessN: number; excessExitN: number; noData: number };
}): JSX.Element | null {
  if (tot.excessN === 0 && tot.excessCost <= 0.004) return null;
  return (
    <p className="muted" data-testid="stranded-excess" data-cost={tot.excessCost} data-n={tot.excessN}>
      Carve-out side: <strong>{fmtMoney(tot.excessCost)}</strong> of excess capacity transfers with
      partly-dedicated people ({fmt(tot.excessN)} {tot.excessN === 1 ? 'carrier' : 'carriers'},{' '}
      {fmt(tot.excessExitN)} already marked for a Day 2 exit) — reverse-TSA backing, or rightsizing
      candidates.
      {tot.noData > 0 && (
        <>
          {' '}
          {fmt(tot.noData)} {tot.noData === 1 ? 'person has' : 'people have'} no dedication recorded and
          are in neither figure.
        </>
      )}
    </p>
  );
}
